import { useCallback, useMemo } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { glossaryKeys } from '@/shared/lib/query/queryKeys/glossary';
import type { OwnerEditHook } from '@/shared/types/editHooks';
import type { UpdateOwnerItem } from '@/shared/types/owner';

import { useOwnerEdit } from '@/entities/owner';

import type { GetGlossaryTermNameResponse } from '@/features/glossary/api/getGlossaryTermName';

type UseGlossaryOwnerEditProps = {
  glossaryId?: string;
  glossaryCategory?: string;
};

export const useGlossaryOwnerEdit = ({ glossaryId, glossaryCategory }: UseGlossaryOwnerEditProps): OwnerEditHook => {
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => (glossaryCategory ? glossaryKeys.categoryInfo(glossaryCategory) : []),
    [glossaryCategory]
  );

  const getOwnersFromData = useCallback((data: GetGlossaryTermNameResponse | undefined): UpdateOwnerItem[] => {
    if (!data) return [];
    return data.owners;
  }, []);

  const invalidateQuery = useCallback(() => {
    if (glossaryCategory) {
      queryClient.invalidateQueries({ queryKey: glossaryKeys.categoryInfo(glossaryCategory) });
    }
  }, [queryClient, glossaryCategory]);

  const ownerEdit = useOwnerEdit<GetGlossaryTermNameResponse>({
    resourceId: glossaryId || '',
    apiPath: '/glossaries',
    queryKey,
    getOwnersFromData,
    invalidateQuery,
  });

  if (!glossaryId || !glossaryCategory) {
    return {
      isOpen: false,
      activeTab: 'teams',
      selectedTeamId: null,
      selectedUserIds: [],
      teamOwners: [],
      userOwners: [],
      currentOwners: [],
      isLoadingTeams: false,
      isLoadingUsers: false,
      isUpdating: false,
      hasChanges: false,
      handleOpen: () => {},
      handleClose: () => {},
      setActiveTab: () => {},
      handleTeamSelect: () => {},
      handleUserSelect: () => {},
      handleUpdate: () => {},
    };
  }

  return ownerEdit;
};
